import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Button, Chip, CircularProgress, Grid, Switch, Typography } from '@material-ui/core';
import InputBase from 'components/input/Form';
import NativeSelect from 'components/select/Native';
import { useDispatch } from 'react-redux';
import { enqueueSnackbar } from 'notifier/actions';
import {
    useAdminMerchantSubscriptionChargesQuery,
    useAdminMerchantSubscriptionListQuery,
    useCancelMerchantSubscriptionMutation,
    useCreateMerchantSubscriptionPricesMutation
} from 'services/payment';
import { useQueryClient } from '@tanstack/react-query';
import ChargesTable from 'views/subscription/ChargesTable';

const INTERVAL_OPTIONS = [
    { label: 'Monthly', value: 'monthly' },
    { label: 'Yearly', value: 'yearly' }
];

const CURRENCY_OPTIONS = [
    { label: 'CAD', value: 'cad' },
    { label: 'USD', value: 'usd' }
];

const useStyles = makeStyles((theme) => ({
    root: {
        maxWidth: 860
    },
    row: {
        minHeight: 58
    },
    note: {
        color: theme.colors.steel,
        marginBottom: theme.spacing(2)
    },
    btnSubmit: {
        position: 'relative'
    },
    buttonProgress: {
        color: theme.palette.common.white,
        position: 'absolute',
        top: '50%',
        left: '50%',
        marginTop: -10,
        marginLeft: -10
    },
    sectionTitle: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(1)
    },
    tableHeader: {
        padding: theme.spacing(1),
        borderBottom: `1px solid ${theme.colors.border}`,
        background: theme.colors.ghostwhite
    },
    tableRow: {
        padding: theme.spacing(1),
        borderBottom: `1px solid ${theme.colors.border}`,
        background: theme.palette.common.white
    },
    empty: {
        color: theme.colors.steel,
        padding: theme.spacing(2),
        background: theme.palette.common.white,
        border: `1px solid ${theme.colors.border}`,
        borderRadius: 8
    }
}));

function formatDateInput(value) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toISOString().slice(0, 10);
}

function toMinorUnit(value) {
    return Math.round(Number(value || 0) * 100);
}

function toIsoDate(value) {
    if (!value) return null;
    const date = new Date(`${value}T00:00:00`);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function formatDate(value) {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString();
}

function formatStatus(status) {
    return status || 'unknown';
}

function Subscription({ merchant }) {
    const classes = useStyles();
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const [monthlyAmount, setMonthlyAmount] = React.useState('');
    const [yearlyAmount, setYearlyAmount] = React.useState('');
    const [currency, setCurrency] = React.useState('cad');
    const [defaultInterval, setDefaultInterval] = React.useState('monthly');
    const [requireSubscription, setRequireSubscription] = React.useState(true);
    const [subscriptionStartAt, setSubscriptionStartAt] = React.useState('');
    const merchantId = merchant?.id;
    const subscriptionListQuery = useAdminMerchantSubscriptionListQuery({
        merchantId,
        options: {
            enabled: !!merchantId
        }
    });
    const chargesQuery = useAdminMerchantSubscriptionChargesQuery({
        merchantId,
        options: {
            enabled: !!merchantId
        }
    });
    const createPricesMutation = useCreateMerchantSubscriptionPricesMutation({
        onSuccess: () => {
            dispatch(
                enqueueSnackbar({
                    message: 'Subscription prices configured successfully',
                    type: 'success'
                })
            );
        },
        onError: (error) => {
            dispatch(
                enqueueSnackbar({
                    message: error?.message || 'Configure subscription prices failed',
                    type: 'info'
                })
            );
        }
    });
    const cancelSubscriptionMutation = useCancelMerchantSubscriptionMutation({
        onSuccess: () => {
            queryClient.invalidateQueries(['merchant-subscription-list', merchantId]);
            dispatch(
                enqueueSnackbar({
                    message: 'Subscription cancellation updated successfully',
                    type: 'success'
                })
            );
        },
        onError: (error) => {
            dispatch(
                enqueueSnackbar({
                    message: error?.message || 'Cancel subscription failed',
                    type: 'info'
                })
            );
        }
    });

    React.useEffect(() => {
        setCurrency((merchant?.paymentCurrency || 'cad')?.toLowerCase());
        setRequireSubscription(merchant?.requireSubscription ?? true);
        setSubscriptionStartAt(formatDateInput(merchant?.subscriptionStartAt));
        setMonthlyAmount('');
        setYearlyAmount('');
        setDefaultInterval('monthly');
    }, [merchant]);

    const isValid = React.useMemo(() => {
        return !!merchant?.id && toMinorUnit(monthlyAmount) > 0 && toMinorUnit(yearlyAmount) > 0 && !!currency;
    }, [merchant, monthlyAmount, yearlyAmount, currency]);

    const handleSave = React.useCallback(async () => {
        if (!isValid) return;

        createPricesMutation.mutate({
                merchantId: Number(merchantId),
            monthlyAmount: toMinorUnit(monthlyAmount),
            yearlyAmount: toMinorUnit(yearlyAmount),
            currency,
            defaultInterval,
            requireSubscription,
            subscriptionStartAt: toIsoDate(subscriptionStartAt)
        });
    }, [
        createPricesMutation,
        currency,
        defaultInterval,
        isValid,
        merchantId,
        monthlyAmount,
        requireSubscription,
        subscriptionStartAt,
        yearlyAmount
    ]);

    const handleCancelSubscription = React.useCallback(
        ({ cancelImmediately }) => {
            if (!merchantId) return;
            const message = cancelImmediately
                ? 'Cancel this subscription immediately?'
                : 'Cancel this subscription at period end?';
            if (!window.confirm(message)) return;

            cancelSubscriptionMutation.mutate({
                merchantId: Number(merchantId),
                cancelImmediately
            });
        },
        [cancelSubscriptionMutation, merchantId]
    );

    const subscriptions = subscriptionListQuery.data || [];

    return (
        <Grid className={classes.root} container direction="column" spacing={2}>
            <Grid item>
                <Typography className={classes.note} variant="body2">
                    Create a merchant-specific Stripe product price config. Amounts are entered in dollars and sent to
                    Stripe as minor units.
                </Typography>
            </Grid>

            <Grid item className={classes.row}>
                <Grid container direction="row" wrap="nowrap" alignItems="center">
                    <Grid item xs={4}>
                        <Typography variant="body2">Require subscription</Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Switch
                            color="primary"
                            checked={!!requireSubscription}
                            onChange={(e) => setRequireSubscription(e.target.checked)}
                        />
                    </Grid>
                </Grid>
            </Grid>

            <Grid item className={classes.row}>
                <Grid container direction="row" wrap="nowrap" alignItems="center">
                    <Grid item xs={4}>
                        <Typography variant="body2">Monthly price</Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <InputBase
                            fullWidth
                            placeholder="49.00"
                            type="number"
                            value={monthlyAmount}
                            onChange={(e) => setMonthlyAmount(e?.target?.value)}
                            error={!!monthlyAmount && toMinorUnit(monthlyAmount) <= 0}
                            errorText="Monthly price must be greater than 0"
                        />
                    </Grid>
                </Grid>
            </Grid>

            <Grid item className={classes.row}>
                <Grid container direction="row" wrap="nowrap" alignItems="center">
                    <Grid item xs={4}>
                        <Typography variant="body2">Yearly price</Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <InputBase
                            fullWidth
                            placeholder="499.00"
                            type="number"
                            value={yearlyAmount}
                            onChange={(e) => setYearlyAmount(e?.target?.value)}
                            error={!!yearlyAmount && toMinorUnit(yearlyAmount) <= 0}
                            errorText="Yearly price must be greater than 0"
                        />
                    </Grid>
                </Grid>
            </Grid>

            <Grid item className={classes.row}>
                <Grid container direction="row" wrap="nowrap" alignItems="center">
                    <Grid item xs={4}>
                        <Typography variant="body2">Currency</Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <NativeSelect
                            selectedValue={currency}
                            onChange={(e) => setCurrency(e.target.value)}
                            options={CURRENCY_OPTIONS}
                        />
                    </Grid>
                </Grid>
            </Grid>

            <Grid item className={classes.row}>
                <Grid container direction="row" wrap="nowrap" alignItems="center">
                    <Grid item xs={4}>
                        <Typography variant="body2">Default interval</Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <NativeSelect
                            selectedValue={defaultInterval}
                            onChange={(e) => setDefaultInterval(e.target.value)}
                            options={INTERVAL_OPTIONS}
                        />
                    </Grid>
                </Grid>
            </Grid>

            <Grid item className={classes.row}>
                <Grid container direction="row" wrap="nowrap" alignItems="center">
                    <Grid item xs={4}>
                        <Typography variant="body2">Subscription start date</Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <InputBase
                            fullWidth
                            type="date"
                            value={subscriptionStartAt}
                            onChange={(e) => setSubscriptionStartAt(e?.target?.value)}
                        />
                    </Grid>
                </Grid>
            </Grid>

            <Grid item>
                <Grid container justifyContent="flex-end">
                    <Grid item className={classes.btnSubmit}>
                        <Button
                            disabled={!isValid || createPricesMutation.isLoading}
                            size="small"
                            onClick={handleSave}
                            variant="contained"
                            color="primary"
                        >
                            {createPricesMutation.isLoading ? (
                                <CircularProgress size={20} className={classes.buttonProgress} />
                            ) : (
                                'Save'
                            )}
                        </Button>
                    </Grid>
                </Grid>
            </Grid>

            <Grid item>
                <Typography className={classes.sectionTitle} variant="body1">
                    Subscriptions
                </Typography>
                {subscriptionListQuery.isLoading ? (
                    <CircularProgress size={24} />
                ) : subscriptions.length ? (
                    <Grid container direction="column">
                        <Grid item className={classes.tableHeader}>
                            <Grid container alignItems="center" spacing={1}>
                                <Grid item xs={2}>
                                    <Typography variant="body1">Status</Typography>
                                </Grid>
                                <Grid item xs={2}>
                                    <Typography variant="body1">Interval</Typography>
                                </Grid>
                                <Grid item xs={2}>
                                    <Typography variant="body1">Period End</Typography>
                                </Grid>
                                <Grid item xs={2}>
                                    <Typography variant="body1">Cancel</Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography variant="body1">Actions</Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                        {subscriptions.map((subscription) => {
                            const canCancel = ['active', 'trialing', 'past_due', 'incomplete'].includes(
                                subscription?.status
                            );
                            return (
                                <Grid item className={classes.tableRow} key={subscription?.id}>
                                    <Grid container alignItems="center" spacing={1}>
                                        <Grid item xs={2}>
                                            <Chip size="small" label={formatStatus(subscription?.status)} />
                                        </Grid>
                                        <Grid item xs={2}>
                                            <Typography variant="body2">{subscription?.interval || '-'}</Typography>
                                        </Grid>
                                        <Grid item xs={2}>
                                            <Typography variant="body2">
                                                {formatDate(subscription?.currentPeriodEnd)}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={2}>
                                            <Typography variant="body2">
                                                {subscription?.cancelAtPeriodEnd ? 'At period end' : '-'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <Grid container spacing={1}>
                                                <Grid item>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="primary"
                                                        disabled={
                                                            !canCancel ||
                                                            subscription?.cancelAtPeriodEnd ||
                                                            cancelSubscriptionMutation.isLoading
                                                        }
                                                        onClick={() =>
                                                            handleCancelSubscription({ cancelImmediately: false })
                                                        }
                                                    >
                                                        Cancel at period end
                                                    </Button>
                                                </Grid>
                                                <Grid item>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="secondary"
                                                        disabled={!canCancel || cancelSubscriptionMutation.isLoading}
                                                        onClick={() =>
                                                            handleCancelSubscription({ cancelImmediately: true })
                                                        }
                                                    >
                                                        Cancel now
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            );
                        })}
                    </Grid>
                ) : (
                    <Typography className={classes.empty} variant="body2">
                        No subscription history for this merchant.
                    </Typography>
                )}
            </Grid>

            <Grid item>
                <ChargesTable
                    charges={chargesQuery.data || []}
                    isLoading={chargesQuery.isLoading}
                    error={chargesQuery.error}
                />
            </Grid>
        </Grid>
    );
}

export default React.memo(Subscription);
